import React from 'react';
import { motion } from 'framer-motion';
import { FaTools, FaHeadset, FaShieldAlt, FaServer, FaCheckCircle, FaClock, FaChartLine } from 'react-icons/fa';
import { getBrand } from '../utils/brandStyles';
import PageHero from '../components/PageHero';
import CTABanner from '../components/CTABanner';

const ManagedIT = ({ market }) => {
  const b = getBrand(market);
  const ctaLink = market === 'lu' ? '/services/assessment' : '/contact';

  // 4 services → 2×2 grid
  const services = [
    {
      icon: <FaHeadset size={32} color={b.accent} />,
      title: 'Help Desk Support',
      description: 'Responsive technical support when you need it.',
      features: ['Email & phone support', 'Remote troubleshooting', 'Ticket tracking system', 'Fast response times']
    },
    {
      icon: <FaServer size={32} color={b.accent} />,
      title: 'Infrastructure Monitoring',
      description: '24/7 monitoring to prevent issues before they impact you.',
      features: ['Server & network monitoring', 'Proactive alerts', 'Performance optimisation', 'Uptime reporting']
    },
    {
      icon: <FaShieldAlt size={32} color={b.accent} />,
      title: 'Security Management',
      description: 'Ongoing protection from threats and vulnerabilities.',
      features: ['Patch management', 'Antivirus & malware protection', 'Firewall management', 'Security updates']
    },
    {
      icon: <FaTools size={32} color={b.accent} />,
      title: 'System Maintenance',
      description: 'Regular maintenance to keep systems running smoothly.',
      features: ['Software updates', 'Backup verification', 'Performance tuning', 'Preventive maintenance']
    }
  ];

  // 6 benefits → 3-col grid
  const benefits = [
    'Predictable monthly IT costs',
    'Reduce downtime and disruptions',
    'Access to expert IT team without hiring',
    'Focus on business, not IT problems',
    'Faster issue resolution',
    'Proactive problem prevention'
  ];

  // 3 pricing plans (lu only) → 3-col grid
  const pricing = market === 'lu' ? [
    {
      name: 'Essential',
      price: 'From €300/month',
      featured: false,
      features: ['Email support (8am–6pm)', 'Remote troubleshooting', 'Monthly system check', 'Security patch management']
    },
    {
      name: 'Business',
      price: 'From €600/month',
      featured: true,
      features: ['Priority email & phone support', '24/7 monitoring', 'Backup management', 'Quarterly IT reviews', 'Vendor management']
    },
    {
      name: 'Premium',
      price: 'Custom pricing',
      featured: false,
      features: ['Dedicated account manager', '24/7 monitoring & support', 'Strategic IT planning', 'Unlimited support requests', 'On-site visits']
    }
  ] : null;

  return (
    <div style={{ fontFamily: b.fontBody, color: b.text, background: b.bg }}>

      {/* ── HERO ── */}
      <PageHero
        market={market}
        title="Managed IT Services"
        subtitle={market === 'lu'
          ? 'Reliable IT support for Luxembourg small businesses. Predictable costs, proactive support — so you can focus on running your business.'
          : 'Comprehensive IT management so you can focus on your business while we handle the technology.'}
        ctaText={market === 'lu' ? 'Get Free IT Assessment' : 'Explore Managed IT'}
        ctaLink={ctaLink}
        icon={<FaClock color="#1A237E" />}
      />

      {/* ── SERVICES — 4 → 2×2 ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>What's Included</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Everything you need, managed for you
            </h2>
          </div>
          <div className="grid-4">
            {services.map((svc, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.07 }} viewport={{ once: true }}
                className={b.isMWO ? 'feature-card mwo-card' : 'feature-card mindit-card'}
                style={{ background: b.bgCard, borderRadius: b.cardRadius, boxShadow: b.cardShadow, padding: '32px 24px' }}
              >
                <div style={{ marginBottom: '16px' }}>{svc.icon}</div>
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.textHeading, marginBottom: '10px' }}>{svc.title}</h3>
                <p style={{ fontSize: b.fz.sm, lineHeight: 1.7, color: b.textMuted, marginBottom: '16px' }}>{svc.description}</p>
                <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {svc.features.map((f, j) => (
                    <li key={j} style={{ display: 'flex', gap: '8px', fontSize: b.fz.xs, color: b.textMuted, alignItems: 'flex-start' }}>
                      <span style={{ color: b.accent, flexShrink: 0 }}>→</span> {f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BENEFITS — 6 → 3-col ── */}
      <section style={{ background: b.bg, padding: b.secPad }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Why Managed IT</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              The business case for managed IT
            </h2>
          </div>
          <div className="grid-6">
            {benefits.map((benefit, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -16 }} whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.35, delay: i * 0.06 }} viewport={{ once: true }}
                className="checklist-item"
                style={{ background: b.checklistBg, borderRadius: b.isMWO ? '0' : '8px', borderLeft: b.isMWO ? `3px solid ${b.accent}` : 'none' }}
              >
                <FaCheckCircle size={16} color={b.accent} className="checklist-icon" />
                <span style={{ fontSize: b.fz.sm, lineHeight: 1.6, fontWeight: 500 }}>{benefit}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COMPARISON ── */}
      <section style={{ background: b.bgAlt, padding: b.secPad }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <span className="eyebrow" style={{ color: b.eyebrowColor }}>Comparison</span>
            <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em' }}>
              Managed IT vs break-fix support
            </h2>
          </div>
          <div className="grid-2">
            <div style={{ background: b.isMWO ? '#1a0c0c' : '#FFF5F5', borderRadius: b.cardRadius, padding: '32px 28px', borderLeft: '3px solid #FCA5A5' }}>
              <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.isMWO ? '#FFB5B5' : '#991B1B', marginBottom: '16px' }}>
                Break-Fix Model
              </h3>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {['Pay per incident (unpredictable)', 'Reactive — fix after problems occur', 'Downtime costs money', 'No ongoing monitoring', 'Higher long-term total cost'].map((item, i) => (
                  <li key={i} style={{ display: 'flex', gap: '8px', fontSize: b.fz.sm, color: b.isMWO ? 'rgba(255,181,181,0.7)' : '#6B2121', alignItems: 'flex-start' }}>
                    <span style={{ flexShrink: 0 }}>✕</span> {item}
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ background: b.isMWO ? '#0c1a0d' : '#F0FFF4', borderRadius: b.cardRadius, padding: '32px 28px', borderLeft: '3px solid #4CAF50' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <FaChartLine size={20} color="#4CAF50" />
                <h3 style={{ fontFamily: b.fontDisplay, fontSize: b.fz.lg, color: b.isMWO ? '#A7F3A0' : '#166534' }}>
                  Managed IT
                </h3>
              </div>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {['Fixed monthly cost (predictable)', 'Proactive — prevent problems before they occur', 'Minimise downtime', '24/7 monitoring included', 'Lower total cost of ownership'].map((item, i) => (
                  <li key={i} style={{ display: 'flex', gap: '8px', fontSize: b.fz.sm, color: b.isMWO ? 'rgba(167,243,160,0.7)' : '#166534', alignItems: 'flex-start' }}>
                    <span style={{ flexShrink: 0 }}>✓</span> {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRICING (lu only) — 3-col ── */}
      {pricing && (
        <section style={{ background: b.bg, padding: b.secPad }}>
          <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <span className="eyebrow" style={{ color: b.eyebrowColor }}>Pricing</span>
              <h2 style={{ fontFamily: b.fontDisplay, fontSize: b.h2size, color: b.textHeading, letterSpacing: '-0.02em', marginBottom: '10px' }}>
                Transparent pricing
              </h2>
              <p style={{ color: b.textMuted, maxWidth: '480px', margin: '0 auto', fontSize: b.fz.base }}>
                Choose the support level that fits your needs and budget
              </p>
            </div>
            <div className="grid-3">
              {pricing.map((plan, i) => (
                <motion.div key={i}
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: i * 0.08 }} viewport={{ once: true }}
                  style={{
                    background: plan.featured ? (b.isMWO ? '#0a1929' : '#EFF6FF') : b.bgCard,
                    borderRadius: b.cardRadius === '0' ? '0' : '12px',
                    boxShadow: plan.featured ? '0 8px 32px rgba(0,188,212,0.2)' : b.cardShadow,
                    border: plan.featured ? `2px solid ${b.accent}` : `1px solid ${b.borderCard}`,
                    padding: '36px 28px',
                    textAlign: 'center',
                    position: 'relative'
                  }}
                >
                  {plan.featured && (
                    <div style={{
                      position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)',
                      background: b.accent, color: b.isMWO ? '#0D0F14' : '#fff',
                      padding: '4px 18px', fontSize: '11px', fontWeight: 700,
                      fontFamily: b.fontDisplay, letterSpacing: '0.08em', textTransform: 'uppercase'
                    }}>Most Popular</div>
                  )}
                  <h3 style={{ fontFamily: b.fontDisplay, fontSize: '1.2rem', color: b.textHeading, marginBottom: '10px' }}>{plan.name}</h3>
                  <div style={{ fontSize: '1.5rem', fontWeight: 700, color: b.accent, marginBottom: '24px', fontFamily: b.fontDisplay }}>
                    {plan.price}
                  </div>
                  <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '12px', textAlign: 'left' }}>
                    {plan.features.map((f, j) => (
                      <li key={j} style={{ display: 'flex', gap: '10px', fontSize: b.fz.sm, color: b.textMuted, alignItems: 'flex-start' }}>
                        <FaCheckCircle size={14} color="#4CAF50" style={{ flexShrink: 0, marginTop: '2px' }} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ── */}
      <CTABanner
        market={market}
        title="Ready for reliable IT support?"
        body={market === 'lu'
          ? 'Get a free assessment and discover how managed IT can save you time and money.'
          : "Let's discuss your IT support needs and find the right solution for your business."}
        ctaText="Get Started"
        ctaLink={ctaLink}
      />
    </div>
  );
};

export default ManagedIT;
